/**
 * AIWorkflowSuggesterDialog (P3-D Doc 39).
 *
 * Modal: textarea intent → AI gen Graph JSON → preview + Save & Open Designer.
 */
import { AlertTriangle, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import * as React from "react";

import { aiAssistApi, type SuggestWorkflowResponse } from "@/api/aiAssist";
import { workflowsApi } from "@/api/workflows";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}

const TRIGGER_TYPES = [
  { value: "manual", label: "Manual (chat)" },
  { value: "form_submit", label: "Form submit" },
  { value: "cron", label: "Cron schedule" },
  { value: "webhook", label: "Webhook" },
];

export function AIWorkflowSuggesterDialog({
  open,
  onClose,
  onCreated,
}: Props): JSX.Element | null {
  const pushToast = useUIStore((s) => s.pushToast);
  const navigate = useRouteStore((s) => s.navigate);

  const [intent, setIntent] = React.useState("");
  const [triggerType, setTriggerType] = React.useState("manual");
  const [overrideCode, setOverrideCode] = React.useState("");
  const [generating, setGenerating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [result, setResult] = React.useState<SuggestWorkflowResponse | null>(null);

  React.useEffect(() => {
    if (open) {
      setIntent("");
      setTriggerType("manual");
      setOverrideCode("");
      setResult(null);
    }
  }, [open]);

  const handleGenerate = async (): Promise<void> => {
    if (intent.trim().length < 5) {
      pushToast({ variant: "warning", title: "Mô tả intent quá ngắn" });
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const res = await aiAssistApi.suggestWorkflow({
        intent: intent.trim(),
        trigger_type: triggerType,
      });
      setResult(res);
      if (!res.ok) {
        pushToast({
          variant: "destructive",
          title: "AI gen thất bại",
          description: res.error ?? "Unknown error",
        });
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Generate thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!result?.ok || !result.graph) return;
    const graph = result.graph as Record<string, unknown>;
    const code =
      overrideCode.trim() || (typeof graph.code === "string" ? graph.code : "");
    if (!/^[a-z][a-z0-9_]{1,63}$/.test(code)) {
      pushToast({
        variant: "warning",
        title: "Code không hợp lệ",
        description: "snake_case [a-z][a-z0-9_]{1,63}",
      });
      return;
    }
    setSaving(true);
    try {
      await workflowsApi.create({
        code,
        name: typeof graph.title === "string" ? graph.title : code,
        description:
          typeof graph.description === "string" ? graph.description : null,
        graph: { ...graph, code } as never,
      });
      pushToast({
        variant: "success",
        title: `Đã tạo workflow ${code}`,
        description: "Đang chuyển sang Designer để review...",
      });
      await onCreated();
      onClose();
      navigate("admin.workflows.edit", code);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu workflow thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const graph = result?.graph as
    | { nodes?: Array<{ id: string; type: string; label?: string }>; edges?: unknown[] }
    | undefined;
  const nodeCount = graph?.nodes?.length ?? 0;
  const edgeCount = graph?.edges?.length ?? 0;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border bg-popover shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b px-4 py-3 bg-gradient-mesh">
          <div className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" />
            <h2 className="text-sm font-semibold">AI Workflow Suggester</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {/* Intent */}
          <div>
            <label className="text-xs font-medium block mb-1">
              Mô tả workflow bạn muốn:
            </label>
            <Textarea
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              rows={4}
              placeholder='VD: "Workflow phê duyệt PR cho phòng IT. PR > 50tr cần CFO duyệt thêm sau khi manager duyệt."'
              autoFocus
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Càng chi tiết, AI gen càng đúng. Mention số bước, branch điều kiện, người duyệt nếu có.
            </p>
          </div>

          {/* Trigger type */}
          <div>
            <label className="text-xs font-medium block mb-1">Trigger</label>
            <select
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value)}
              className="w-full h-8 rounded-md border bg-background px-2 text-sm"
            >
              {TRIGGER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Generate button */}
          {!result ? (
            <Button
              variant="brand"
              className="w-full"
              onClick={() => void handleGenerate()}
              disabled={generating || intent.trim().length < 5}
            >
              {generating ? (
                <>
                  <Loader2 className="size-4 mr-1 animate-spin" />
                  AI đang gen workflow...
                </>
              ) : (
                <>
                  <Sparkles className="size-4 mr-1" /> Generate workflow
                </>
              )}
            </Button>
          ) : null}

          {/* Result */}
          {result ? (
            <div className="space-y-3 border-t pt-4">
              {result.ok ? (
                <>
                  <div className="rounded-md border bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 p-3 space-y-1">
                    <div className="text-sm font-semibold flex items-center gap-1.5">
                      <Sparkles className="size-3.5 text-emerald-600" />
                      Đã gen workflow
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <code className="bg-muted px-1 rounded">
                        {String(graph?.["code" as never] ?? "no_code")}
                      </code>
                      {" · "}
                      <span>{nodeCount} nodes · {edgeCount} edges</span>
                    </div>
                  </div>

                  {/* Explanation */}
                  {result.explanation ? (
                    <div className="text-xs text-foreground bg-muted/50 rounded-md p-2.5">
                      <div className="font-medium mb-0.5">AI giải thích:</div>
                      <p className="text-muted-foreground">{result.explanation}</p>
                    </div>
                  ) : null}

                  {/* Nodes preview */}
                  {graph?.nodes && graph.nodes.length > 0 ? (
                    <div className="border rounded-md p-2 max-h-[160px] overflow-y-auto bg-muted/20">
                      <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">
                        Nodes
                      </div>
                      <ul className="space-y-0.5 text-xs">
                        {graph.nodes.map((n) => (
                          <li key={n.id} className="flex items-center gap-1.5">
                            <span className="inline-block w-16 text-[10px] text-muted-foreground uppercase">
                              {n.type}
                            </span>
                            <code className="text-[10px] bg-muted px-1 rounded">
                              {n.id}
                            </code>
                            <span className="truncate">{n.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {/* Warnings */}
                  {result.warnings && result.warnings.length > 0 ? (
                    <div className="rounded-md border bg-amber-50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-800 p-2.5 text-xs">
                      <div className="flex items-center gap-1.5 font-medium text-amber-900 dark:text-amber-100">
                        <AlertTriangle className="size-3.5" />
                        Cảnh báo ({result.warnings.length})
                      </div>
                      <ul className="mt-1 space-y-0.5 text-amber-900 dark:text-amber-200 list-disc list-inside">
                        {result.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {/* Override code */}
                  <div>
                    <label className="text-[10px] font-medium block mb-0.5 text-muted-foreground">
                      Code workflow (override)
                    </label>
                    <Input
                      value={overrideCode}
                      onChange={(e) =>
                        setOverrideCode(
                          e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9_]/g, "_"),
                        )
                      }
                      placeholder={
                        typeof graph?.["code" as never] === "string"
                          ? String(graph?.["code" as never])
                          : "snake_case_code"
                      }
                      className="font-mono text-sm h-8"
                    />
                  </div>
                </>
              ) : (
                /* Error state */
                <div className="rounded-md border bg-rose-50 dark:bg-rose-950/20 border-rose-300 dark:border-rose-800 p-3 space-y-1">
                  <div className="text-sm font-semibold text-rose-900 dark:text-rose-100 flex items-center gap-1.5">
                    <AlertTriangle className="size-3.5" />
                    AI gen thất bại
                  </div>
                  <p className="text-xs text-rose-800 dark:text-rose-200">
                    {result.error ?? "Unknown error"}
                  </p>
                  {result.raw_text ? (
                    <details className="text-[10px] mt-2">
                      <summary className="cursor-pointer text-muted-foreground">
                        Xem raw response
                      </summary>
                      <pre className="mt-1 bg-background border rounded p-2 overflow-x-auto whitespace-pre-wrap text-[10px]">
                        {result.raw_text}
                      </pre>
                    </details>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </div>

        <footer className="flex justify-between gap-2 border-t px-4 py-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Huỷ
          </Button>
          <div className="flex gap-2">
            {result ? (
              <Button
                variant="outline"
                onClick={() => void handleGenerate()}
                disabled={generating || saving}
              >
                <RefreshCw className="size-3.5 mr-1" />
                Regenerate
              </Button>
            ) : null}
            {result?.ok ? (
              <Button
                variant="brand"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="size-3.5 mr-1 animate-spin" />
                    Đang lưu...
                  </>
                ) : (
                  "Save & Open Designer"
                )}
              </Button>
            ) : null}
          </div>
        </footer>
      </div>
    </div>
  );
}
